/* eslint-disable camelcase */
import React from 'react';

import styled from '@emotion/styled';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Spinner } from '@lightspeed/flame/Spinner';
import { Area, ComposedChart, Legend, Line, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

type CostVsSalesChartProps = {
  data?: {
    period: string;
    total_cost: string;
    total_sale: string;
  }[];
  isLoading?: boolean;
  isError?: boolean;
};

const CustomTooltip = ({ active, payload = [] }: TooltipProps<ValueType, NameType>) => {
  if (!active || payload.length === 0) return null;
  return (
    <div
      style={{
        backgroundColor: '#020203',
        textAlign: 'right',
        padding: '10px 5px',
        borderRadius: '4px',
        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
      }}
    >
      {payload.map((pld) => {
        if (pld && typeof pld.name === 'string' && !pld.name.endsWith('area')) {
          return (
            <p
              key={pld.name}
              style={{
                color: '#fff',
                padding: 0,
                margin: '2px',
                fontSize: '12px',
                fontWeight: 'normal',
              }}
            >
              {pld.name === 'total_cost' ? `Receiving costs` : 'Sales'}:{' '}
              {typeof pld.value === 'number' ||
              (typeof pld.value === 'string' && !isNaN(Number(pld.value)))
                ? Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                  }).format(Number(pld.value))
                : ''}
            </p>
          );
        }
        return null;
      })}
    </div>
  );
};

const CustomLegend = ({ payload = [] }: TooltipProps<ValueType, NameType>) => {
  if (payload.length === 0) return null;
  return (
    <Box
      style={{
        marginTop: '-25px',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        gap: '20px',
      }}
    >
      {payload.map((entry) => {
        if (entry && typeof entry.value === 'string' && !entry.value.endsWith('area')) {
          return (
            <Box key={entry.value} style={{ display: 'flex', flexDirection: 'row' }}>
              <Box
                style={{
                  width: '20px',
                  height: '1px',
                  backgroundColor: entry.color,
                  marginRight: '5px',
                  marginTop: '7px',
                }}
              />
              <Box style={{ color: '#000', fontSize: '12px' }}>
                {entry.value === 'total_cost' ? 'Cost of received goods' : 'Total sales'}
              </Box>
            </Box>
          );
        }
        return null;
      })}
    </Box>
  );
};

const CustomBoxContainer = styled(Box)`
  border: 1px solid #d7dce1;
  border-radius: 4px;
  padding: 10px;
`;

export default function CostVsSalesChart({ data, isError, isLoading }: CostVsSalesChartProps) {
  if (isLoading || isError || !data || data.length === 0) {
    return (
      <Flex alignItems="center" justifyContent="center" width={520} height={382}>
        <Box alignItems="center">
          {isLoading && <Spinner data-test="spinner" size="2rem" color="gray-300" />}
          {!isLoading && <Box data-test="error">Error loading data</Box>}
        </Box>
      </Flex>
    );
  }

  // for visual effect we remove the first and last tick from the x-axis
  if (data && data.length > 1) {
    data[0].period = '';
    data[data.length - 1].period = '';
  }

  // We cast the total_cost and total_sale to numbers
  const formattedData = data.map((d) => ({
    ...d,
    total_cost: isNaN(Number(d.total_cost)) ? 0 : Number(d.total_cost),
    total_sale: isNaN(Number(d.total_sale)) ? 0 : Number(d.total_sale),
  }));

  // We get the max value of the total_sale and add a little padding to the Yaxis so that it doesn't touch the top
  const maxTotalSale = Math.max(...formattedData.map((d) => d.total_sale)) * 1.05;

  return (
    <CustomBoxContainer data-test="chart">
      <ComposedChart
        width={520}
        height={382}
        data={formattedData}
        margin={{
          top: 30,
          right: 30,
          left: 10,
          bottom: 15,
        }}
      >
        <XAxis
          dataKey="period"
          label={{
            value: 'Month (2023)',
            angle: 0,
            position: 'insideBottom',
            offset: -15,
            fontWeight: 'bold',
            fill: 'black',
          }}
          tickFormatter={(value) => (value === '' ? '' : `${value}`)}
        />

        <YAxis
          label={{
            value: 'Dollars ($)',
            angle: -90,
            offset: 3,
            position: 'insideLeft',
            fontWeight: 'bold',
            fill: 'black',
          }}
          domain={[0, maxTotalSale]}
          tickFormatter={(value) => {
            if (value === 0) {
              return '';
            }
            return Intl.NumberFormat('en-US', {
              notation: 'compact',
              compactDisplay: 'short',
            }).format(value);
          }}
        />
        <defs>
          <linearGradient id="fillGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="15%" stopColor="#FDD6D7" stopOpacity={1} />
            <stop offset="85%" stopColor="#fff" stopOpacity={1} />
          </linearGradient>
        </defs>
        <Tooltip cursor={false} content={<CustomTooltip />} />
        <Legend verticalAlign="top" content={<CustomLegend />} />

        <Area
          name="total_cost-area"
          legendType="none"
          type="linear"
          dataKey="total_cost"
          stroke="none"
          fillOpacity={1}
          fill="url(#fillGradient)"
          activeDot={false}
        />
        <Area
          name="total_sale-area"
          legendType="none"
          type="linear"
          dataKey="total_sale"
          stroke="none"
          fillOpacity={1}
          fill="white"
          activeDot={false}
        />
        <Line
          legendType="none"
          type="linear"
          dataKey="total_cost"
          stroke="#2E61DE"
          activeDot={{ r: 3, stroke: '#2E61DE' }}
          dot={false}
          strokeWidth={2}
        />
        <Line
          strokeWidth={2}
          legendType="none"
          type="linear"
          dataKey="total_sale"
          stroke="#DE2E37"
          dot={false}
          activeDot={false}
        />
      </ComposedChart>
    </CustomBoxContainer>
  );
}
